import PropTypes from 'prop-types';
import React, { FC, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useHistory, useLocation, withRouter } from 'react-router';
import { type RouteComponentProps } from 'react-router-dom';
import { ReduxState } from 'types';
import {
  getWizardType,
  PERFORMANCE_FEATURE_ASSESS_ORGANIZATION,
  PERFORMANCE_FEATURE_ASSESS_ORGANIZATION_OPEN_RESPONSE_QUESTIONS,
  PERFORMANCE_FEATURE_EMPLOYEE_NPS_QUESTION_ENABLED,
  PERFORMANCE_FEATURE_EMPLOYEE_NPS_QUESTION_OPTIONAL,
  PERFORMANCE_FEATURE_HIDE_PULSE_ANONYMOUS_DESCRIPTION,
  WIZARD_TYPE_ENPS_ONLY,
} from 'utils/models/PerformanceUtils';
import { applyTargetFiltersToQuestions } from 'views/Widgets/People/Filters/common';
import { setCurrentPerfSurveyResponse } from '../../actions';
import {
  CAMPAIGN_STATUSES,
  getFeatureForPhase,
} from '../../utils/models/Campaign';
import {
  ASSESS_ORGANIZATION_EMPLOYEE_NPS_COMMENT,
  ASSESS_ORGANIZATION_EMPLOYEE_NPS_QUESTION,
  addAnonymousResponseValues,
  addCustomResponseValues,
  DEFAULT_ASSESS_ORGANIZATION_OPEN_RESPONSE_QUESTIONS,
  extractAnonymousResponses,
  extractCustomResponses,
  getAutoFocusIndex,
  getCampaignFeature,
  getPerformanceFeatureEnabled,
  getStepNumber,
  perfCampaignCallback,
  prepareOpenResponseQuestion,
} from '../../utils/models/Performance';
import ValidatedForm from '../Widgets/Forms/ValidatedForm';
import PerformancePage from './PerformancePage';

const PerformanceStepAssessOrganization: FC<Props> = (props) => {
  const location = useLocation();
  const history = useHistory();
  const { formatMessage } = useIntl();

  const campaign = props.campaign;

  const isDemoOrPreviewMode = useMemo(
    // @ts-expect-error
    () => campaign?.status === CAMPAIGN_STATUSES.DEMO,
    // @ts-expect-error
    [campaign?.status]
  );

  const currentStepNumber = useMemo(
    () =>
      getStepNumber(
        props.me,
        props.currentOrganization,
        campaign,
        props.currentPerfSurveyResponse,
        props.demoPeople,
        location.pathname,
        formatMessage
      ),
    [
      location.pathname,
      props.currentOrganization,
      campaign,
      props.currentPerfSurveyResponse,
      props.demoPeople,
      props.me,
      formatMessage,
    ]
  );

  const unfilteredAssessOrganizationQuestions = useMemo(() => {
    const customAssessOrganizationQuestions = getCampaignFeature(
      campaign,
      PERFORMANCE_FEATURE_ASSESS_ORGANIZATION_OPEN_RESPONSE_QUESTIONS
    );

    // Check if original "Engagement survey" enabled in cycle
    const assessOrganizationEnabled = getPerformanceFeatureEnabled(
      campaign,
      PERFORMANCE_FEATURE_ASSESS_ORGANIZATION
    );

    // @ts-expect-error
    const wizardType = getWizardType(campaign);

    const engagementQuestions =
      assessOrganizationEnabled && wizardType !== WIZARD_TYPE_ENPS_ONLY
        ? (customAssessOrganizationQuestions ??
          DEFAULT_ASSESS_ORGANIZATION_OPEN_RESPONSE_QUESTIONS)
        : [];

    const employeeNPSEnabled = getPerformanceFeatureEnabled(
      campaign,
      PERFORMANCE_FEATURE_EMPLOYEE_NPS_QUESTION_ENABLED
    );

    const employeeNPSQuestions = employeeNPSEnabled
      ? [
          {
            ...ASSESS_ORGANIZATION_EMPLOYEE_NPS_QUESTION,
            required: !getPerformanceFeatureEnabled(
              campaign,
              PERFORMANCE_FEATURE_EMPLOYEE_NPS_QUESTION_OPTIONAL
            ),
            employeeNpsCommentTitle: getFeatureForPhase(
              campaign,
              'O',
              'employee_nps_comment_title'
            ),
          },
          ASSESS_ORGANIZATION_EMPLOYEE_NPS_COMMENT(formatMessage),
        ]
      : [];

    const questions = [...employeeNPSQuestions, ...engagementQuestions];

    const autoFocusIndex = getAutoFocusIndex(questions);
    return questions.map((q, index) =>
      prepareOpenResponseQuestion(
        formatMessage,
        q,
        [],
        false,
        // @ts-expect-error
        props.me.given_name,
        false,
        false,
        campaign,
        props.currentOrganization,
        autoFocusIndex === index,
        index,
        isDemoOrPreviewMode
      )
    );
  }, [
    campaign,
    isDemoOrPreviewMode,
    // @ts-expect-error
    props.me.given_name,
    props.currentOrganization,
    formatMessage,
  ]);

  const assessOrganizationQuestions = useMemo(
    () =>
      applyTargetFiltersToQuestions(
        unfilteredAssessOrganizationQuestions,
        // @ts-expect-error
        props.me?.id
      ),
    // @ts-expect-error
    [unfilteredAssessOrganizationQuestions, props.me?.id]
  );

  const propsSetCurrentPerfSurveyResponse = props.setCurrentPerfSurveyResponse;
  const callback = useCallback(
    (data) => {
      if (data) {
        propsSetCurrentPerfSurveyResponse(data);
        perfCampaignCallback(
          props.me,
          props.currentOrganization,
          campaign,
          props.currentPerfSurveyResponse,
          history,
          props.demoPeople,
          data,
          formatMessage
        );
      }
    },
    [
      history,
      props.currentOrganization,
      campaign,
      props.currentPerfSurveyResponse,
      props.demoPeople,
      props.me,
      propsSetCurrentPerfSurveyResponse,
      formatMessage,
    ]
  );

  const object = useMemo(() => {
    // @ts-expect-error
    const responses = props.currentPerfSurveyResponse?.responses;
    const obj = {};

    if (typeof responses === 'object') {
      addCustomResponseValues(responses, obj);
      addAnonymousResponseValues(responses, obj);
    }

    return obj;
  }, [props.currentPerfSurveyResponse]);

  const transformObjectBeforeSubmit = useCallback(
    (object) => {
      const responses = {
        ...extractCustomResponses(object),
        ...extractAnonymousResponses(object),
      };

      return {
        // @ts-expect-error
        id: props.currentPerfSurveyResponse?.id,
        // @ts-expect-error
        campaign: campaign.id,
        step: currentStepNumber,
        responses: responses,
      };
    },
    // @ts-expect-error
    [currentStepNumber, campaign.id, props.currentPerfSurveyResponse?.id]
  );

  return (
    <PerformancePage
      campaign={campaign}
      title={formatMessage({
        id: 'app.views.performance.performance_step_assess_organization.title.feedback_for_us',
        defaultMessage: 'Feedback for us',
      })}
    >
      <></>
      <div className="mb-5">
        <FormattedMessage
          id="app.views.performance.assess_organization.introduction"
          defaultMessage="Can you tell us about your {organization} experience? Please answer the questions below."
          values={{
            // @ts-expect-error
            organization: props.currentOrganization.name,
          }}
        />{' '}
        {!getCampaignFeature(
          campaign,
          PERFORMANCE_FEATURE_HIDE_PULSE_ANONYMOUS_DESCRIPTION
        ) && (
          <FormattedMessage
            id="app.views.performance.assess_organization.your_responses_are_confidential"
            defaultMessage="Your responses are confidential."
          />
        )}
      </div>
      <ValidatedForm
        draftAutosaveEnabled={!isDemoOrPreviewMode}
        // @ts-expect-error
        uniqueFormKey={`campaign-${campaign.id}-assess-organization`}
        method={'PATCH'}
        url={
          // @ts-expect-error
          campaign.status === CAMPAIGN_STATUSES.DEMO
            ? undefined
            : 'survey-responses'
        }
        callback={callback}
        buttonIsBlock={false}
        buttonClassName="mt-0"
        submitText={formatMessage({
          id: 'app.views.performance.performance_step_assess_organization.submit_text.save_and_continue',
          defaultMessage: 'Save and continue',
        })}
        transformObjectBeforeSubmit={transformObjectBeforeSubmit}
        object={object}
        inputs={assessOrganizationQuestions}
        organization={props.currentOrganization}
      />
    </PerformancePage>
  );
};

const PerformanceStepAssessOrganization_propTypes = {
  me: PropTypes.object.isRequired,
  currentOrganization: PropTypes.object.isRequired,
  currentPerfSurveyResponse: PropTypes.object.isRequired,
  setCurrentPerfSurveyResponse: PropTypes.func.isRequired,
  campaign: PropTypes.object.isRequired,
  setCampaign: PropTypes.func.isRequired,
  demoPeople: PropTypes.arrayOf(PropTypes.object).isRequired,
};

type Props = PropTypes.InferProps<
  typeof PerformanceStepAssessOrganization_propTypes
> &
  RouteComponentProps;

const mapStateToProps = (state: ReduxState) => {
  const { me, currentOrganization, currentPerfSurveyResponse, demoPeople } =
    state;

  return {
    me,
    currentOrganization,
    currentPerfSurveyResponse,
    demoPeople,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setCurrentPerfSurveyResponse: (changes) =>
      dispatch(setCurrentPerfSurveyResponse(changes)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(React.memo(PerformanceStepAssessOrganization)));
